import { promiseState } from '@passionware/platform-react';
import { maybe, rd, type Maybe } from '@passionware/monads';
import { ElementRef, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from 'v5/design-system/Button';
import { DialogBody } from 'v5/design-system/Dialog';
import { Form } from 'v5/design-system/Form';
import { useResponsiveDialogBody } from 'v5/design-system/useResponsiveDialogBody';
import { appScopeUtils } from 'v5/core/app-scope/appScope.types';
import { CalloutDetailsFields } from 'v5/features/callouts/_shared/CalloutDetailsFields';
import { CalloutCreateFormData } from 'v5/features/callouts/callout-create.types';
import { normalizeCalloutSlugForSubmit } from 'v5/api/callouts/callout-slug.logic';
import {
  buildMappingsFromTemplate,
  getTranslatedCalloutTemplates
} from 'v5/features/callouts/callout-templates';
import { CalloutTemplateSelectionStep } from 'v5/features/callouts/steps/CalloutTemplateSelectionStep';
import { ListBox } from 'v5/design-system/ListBox';
import { AsyncButton } from 'v5/patterns/forms/fields/AsyncButton';
import { DialogErrorFooter } from 'v5/patterns/forms/dialogs/DialogErrorFooter';
import { FormDialogContent } from 'v5/patterns/forms/dialogs/FormDialogContent';
import { useRegisterDirtyState } from 'v5/patterns/forms/unsaved-changes/useRegisterDirtyState';
import { cn } from 'v5/platform/dom/cn';
import { WithServices } from 'v5/platform/ts/services';
import { WithResourceTypeService } from 'v5/services/internal/ResourceTypeService/ResourceTypeService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { WithCalloutsService } from 'v5/services/io/CalloutsService/CalloutsService';

type Step = 'details' | 'template';

export interface CreateCalloutDialogContentProps
  extends WithServices<
    [
      WithResourceTypeService,
      WithCalloutsService,
      WithAppScopeService,
      WithEnterpriseService
    ]
  > {
  onSubmit?: (data: CalloutCreateFormData) => Promise<void> | void;
  open?: boolean;
  enterpriseWorkspaceId?: Maybe<number>;
  registerEscapeKeyDownHandler?: (
    handler: ((event: KeyboardEvent) => void) | undefined
  ) => void;
}

export function CreateCalloutDialogContent({
  onSubmit,
  open = true,
  services,
  enterpriseWorkspaceId,
  registerEscapeKeyDownHandler
}: CreateCalloutDialogContentProps) {
  const { t } = useTranslation('callouts');
  const [currentStep, setCurrentStep] = useState<Step>('details');
  const templateListRef = useRef<ElementRef<typeof ListBox>>(null);
  const templates = getTranslatedCalloutTemplates(t);
  const defaultTemplate = templates[0];

  const form = useForm<CalloutCreateFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      title: '',
      slug: '',
      resource_type_id: 0,
      templateId: defaultTemplate?.id ?? 0,
      mappings: defaultTemplate
        ? buildMappingsFromTemplate(defaultTemplate)
        : []
    }
  });

  const mutation = promiseState.useMutation(
    async (data: CalloutCreateFormData) => {
      const slug = await services.calloutsService.resolveUniqueSlug(
        normalizeCalloutSlugForSubmit(data.slug),
        { workspaceId: enterpriseWorkspaceId ?? null }
      );
      return onSubmit?.({ ...data, slug });
    }
  );

  useRegisterDirtyState(form.formState.isDirty);

  const title = form.watch('title');
  const resourceTypeId = form.watch('resource_type_id');
  const { isValid } = form.formState;

  const scopeRd = services.appScopeService.useResolvedAppScope();
  const browsingIdentity = rd.tryMap(
    scopeRd,
    appScopeUtils.identityFromResolved
  );
  const resourceTypesScope = maybe.map(browsingIdentity, identity =>
    enterpriseWorkspaceId != null
      ? {
          level: 'workspace' as const,
          organizationId: identity.organizationId,
          workspaceId: enterpriseWorkspaceId
        }
      : identity
  );
  const resourceTypes =
    services.resourceTypeService.useResourceTypes(resourceTypesScope);
  const responsive = useResponsiveDialogBody(450);

  const dialogClassName = 'max-w-180 min-h-[calc(min(450px,100vh-2rem))]';

  const handleSubmit = (data: CalloutCreateFormData) => {
    if (currentStep === 'details') {
      setCurrentStep('template');
      return;
    }
    mutation.track(data);
  };

  const handleBack = () => {
    setCurrentStep('details');
  };

  useEffect(() => {
    if (!registerEscapeKeyDownHandler) {
      return;
    }

    registerEscapeKeyDownHandler(event => {
      if (!open || currentStep !== 'template') {
        return;
      }
      event.preventDefault();
      handleBack();
    });

    return () => {
      registerEscapeKeyDownHandler(undefined);
    };
  }, [open, currentStep, registerEscapeKeyDownHandler]);

  const getSaveButtonText = (): string => {
    return currentStep === 'details'
      ? t('CalloutDialog.next')
      : t('CalloutDialog.create');
  };

  const canProceed = (): boolean => {
    if (currentStep === 'details') {
      return Boolean(title?.trim()) && isValid && resourceTypeId > 0;
    }
    return true;
  };

  // a hack to focus the selected template in the list when the template step is entered
  // maybe there is a better way to do this?
  useEffect(() => {
    if (currentStep !== 'template') {
      return;
    }

    let cancelled = false;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) {
          return;
        }

        const list = templateListRef.current;
        if (!list) {
          return;
        }

        const selectedItem = list.querySelector<HTMLElement>(
          '[role="option"][aria-selected="true"]'
        );
        (selectedItem ?? list).focus();
      });
    });

    return () => {
      cancelled = true;
    };
  }, [currentStep]);

  return (
    <Form {...form}>
      <FormDialogContent
        onSubmit={form.handleSubmit(handleSubmit)}
        open={open}
        title={t('CalloutDialog.createCallout')}
        saveText={getSaveButtonText()}
        mutation={mutation.state}
        className={dialogClassName}
        style={responsive.containerStyle}
        renderDialogBody={children => (
          <DialogBody
            className={cn('space-y-6', responsive.bodyClassName)}
            scrollerClassName={responsive.scrollerClassName}
          >
            {children}
          </DialogBody>
        )}
        footerContent={
          <>
            {currentStep === 'template' && (
              <Button
                type="button"
                variant="ghost"
                onClick={handleBack}
                className="mr-auto"
              >
                {t('CalloutDialog.backWithArrow')}
              </Button>
            )}
            <DialogErrorFooter mutation={mutation.state} />
          </>
        }
        saveButtonSlot={
          <AsyncButton
            type="submit"
            mutation={mutation.state}
            variant="primary"
            visuallyDisabled={!canProceed()}
          >
            {getSaveButtonText()}
          </AsyncButton>
        }
      >
        {currentStep === 'details' && (
          <div className="space-y-4">
            <CalloutDetailsFields
              form={form}
              resourceTypes={resourceTypes}
              workspaceId={enterpriseWorkspaceId ?? null}
              services={services}
            />
          </div>
        )}
        {currentStep === 'template' && (
          <CalloutTemplateSelectionStep form={form} listRef={templateListRef} />
        )}
      </FormDialogContent>
    </Form>
  );
}
